import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VisualizationMode } from "./types";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { useUser } from "~/modules/user/hooks/useUser";
import { FeatureFlagState } from "~/modules/feature_flag/types";
import { useState, useEffect, useRef } from "react";
import * as Analytics from "~/modules/visualization/analytics";
import { LoginRequiredModal } from "~/modules/downloads/components/LoginRequiredModal";
import { cn } from "~/lib/utils";
import { SelectInput } from "~/components/SelectInput";

// // // //

export interface VisualizationModeControls {
    activeMode: VisualizationMode; // Currently active visualization mode.
    modes: VisualizationMode[]; // Array of all available visualization modes.
    loading: boolean; // Indicates if the mode change is in progress.
    setActiveMode: (updated: VisualizationMode) => void; // Function to update the active mode.
}

// // // //

/**
 * Removes feature-flagged VisualizationMode items if a user isn't allowed to view them
 */
function removeFeatureFlaggedModes(props: {
    modes: VisualizationMode[];
    featureFlagState: FeatureFlagState;
}): VisualizationMode[] {
    const { modes, featureFlagState } = props;

    // Filter groups + nested items based on featureFlagState
    return modes.filter((mode) => {
        const featureFlag = mode.featureFlag;
        if (featureFlag) {
            return featureFlagState[featureFlag];
        }
        return true; // If there's no feature flag specified, include the item
    });
}

// // // //

/**
 * Renders a selector for changing between different visualization modes
 */
export function VisualizationModeSelector(props: {
    modeControls: VisualizationModeControls;
}) {
    const { activeMode: active, setActiveMode: onChange } = props.modeControls;

    const { user, isLoggedIn } = useUser();
    const [showLoginModal, setShowLoginModal] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

    const [indicatorStyle, setIndicatorStyle] = useState({
        left: 0,
        width: 0,
    });

    // Get the filtered groups based on the user's feature flags
    const modes = removeFeatureFlaggedModes({
        modes: props.modeControls.modes,
        featureFlagState: user.features,
    });

    useEffect(() => {
        const updateIndicator = () => {
            const targetEl = buttonRefs.current.get(active.key);
            const containerEl = containerRef.current;

            if (targetEl && containerEl) {
                const containerRect = containerEl.getBoundingClientRect();
                const targetRect = targetEl.getBoundingClientRect();

                setIndicatorStyle({
                    left: targetRect.left - containerRect.left,
                    width: targetRect.width,
                });
            }
        };

        updateIndicator();
        window.addEventListener("resize", updateIndicator);
        const timeoutId = setTimeout(updateIndicator, 50);

        return () => {
            window.removeEventListener("resize", updateIndicator);
            clearTimeout(timeoutId);
        };
    }, [active.key, modes.length]);

    // Do not render the component if there are less than two modes to switch between.
    if (modes.length < 2) {
        return null;
    }

    // Render a button for each mode
    return (
        <>
            <VisualizationModeSelectorMobile
                {...props}
                isLoggedIn={isLoggedIn}
                showLoginModal={(modeKey) => {
                    Analytics.trackTimeSeriesLoginCtaOpen(modeKey);
                    setShowLoginModal(true);
                }}
            />

            {/* The `@*:` variants here resolve against the `@container` on the
                TimeseriesCard control bar — i.e. the width of the card. */}
            <div className="hidden @2xl:flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground whitespace-nowrap select-none">
                    Metric
                </p>

                <div
                    ref={containerRef}
                    className="hidden @2xl:inline-flex w-fit relative gap-1.5 p-1 rounded-lg bg-muted mt-1"
                >
                    {/* Sliding indicator */}
                    {indicatorStyle.width > 0 && (
                        <div
                            className="absolute top-1 bottom-1 bg-card ring-1 ring-slate-400/60 rounded-md shadow-sm transition-all duration-200 ease-in-out"
                            style={{
                                left: `${indicatorStyle.left}px`,
                                width: `${indicatorStyle.width}px`,
                            }}
                        />
                    )}

                    {modes.map((mode) => {
                        const isActive = mode.key === active.key;

                        return (
                            <FeatureFlag
                                key={mode.key}
                                feature={mode.featureFlag}
                            >
                                <button
                                    ref={(el) => {
                                        buttonRefs.current.set(mode.key, el);
                                    }}
                                    onClick={() => {
                                        if (isActive) return;

                                        // If the selected mode requires a login and the user is not logged in, do not allow the change
                                        if (mode.loginRequired && !isLoggedIn) {
                                            Analytics.trackTimeSeriesLoginCtaOpen(
                                                mode.key,
                                            );
                                            setShowLoginModal(true);
                                            return;
                                        }

                                        onChange(mode);
                                    }}
                                    className={cn(
                                        "relative z-10 flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium duration-200 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:ring-1 hover:ring-slate-400/60 select-none",
                                        {
                                            "bg-card text-foreground/80":
                                                isActive,
                                            "text-foreground/50": !isActive,
                                            "hover:text-foreground": !isActive,
                                        },
                                    )}
                                >
                                    {mode.loginRequired && !isLoggedIn && (
                                        <FontAwesomeIcon
                                            icon={faLock}
                                            className="h-2.5"
                                        />
                                    )}
                                    <span>{mode.content.label}</span>
                                </button>
                            </FeatureFlag>
                        );
                    })}
                </div>
            </div>

            {/* Disply active mode's description text */}
            {/* ENHANCEMENT - re-enable this in a way that doesn't break up the UI */}
            {/* {active.content.description && (
                <div className="mt-2 font-light text-slate-600 text-sm flex items-center gap-1.5">
                    <FontAwesomeIcon className="h-4" icon={faInfoCircle} />
                    {active.content.description}
                </div>
            )} */}

            {!isLoggedIn && (
                <LoginRequiredModal
                    allowScroll // NOTE - this is very important. When this is set to false, all the visualizations will resize and performance will crawl for a second.
                    show={showLoginModal}
                    onHide={() => {
                        setShowLoginModal(false);
                    }}
                    analytics={{
                        onConfirmed: () => {
                            Analytics.trackTimeSeriesLoginCtaConfirmed();
                        },
                        onClose: () => {
                            Analytics.trackTimeSeriesLoginCtaClose();
                        },
                    }}
                />
            )}
        </>
    );
}

/**
 * VisualizationModeSelectorMobile
 * Mobile-only mode selector UI
 */
function VisualizationModeSelectorMobile(props: {
    modeControls: VisualizationModeControls;
    isLoggedIn: boolean;
    showLoginModal: (modeKey: string) => void;
}) {
    const { isLoggedIn, showLoginModal } = props;
    const {
        activeMode: active,
        modes,
        setActiveMode: onChange,
    } = props.modeControls;

    return (
        <div className="@2xl:hidden w-full">
            <SelectInput
                size="sm"
                value={active.key}
                placeholder="Metric"
                placeholderPosition="inset"
                onChange={(updatedValue) => {
                    const modeKey = updatedValue;
                    const selectedMode = modes.find((m) => m.key === modeKey);
                    if (!selectedMode) {
                        return;
                    }

                    // If the selected mode requires a login and the user is not logged in, do not allow the change
                    if (selectedMode.loginRequired && !isLoggedIn) {
                        showLoginModal(selectedMode.key);
                        return;
                    }

                    onChange(selectedMode);
                }}
                options={modes.map((mode) => ({
                    value: mode.key,
                    featureFlag: mode.featureFlag,
                    content: {
                        label:
                            mode.loginRequired && !isLoggedIn
                                ? `${mode.content.label} (Login Required)`
                                : mode.content.label,
                    },
                }))}
            />
        </div>
    );
}
